import { Component, Input, ElementRef, ViewChild } from '@angular/core';

import { Observable } from 'rxjs';
import { MessageService } from 'primeng/api';
import { isString } from 'lodash';

import { AbstractAiDialogComponent } from '../../../../../Common/Dialogs/components/AbstractAIDialog/Abstract-AI-dialog.component';
import { ButtonTypesEnum } from '../../../../../Common/Forms/enums/button-types.enum';
import { AbstractAIHelperDialogService } from '../../../../../Common/Dialogs/services/abstract-AI-helper-dialog.service';
import { AiSettingFormModel } from '../../../../../Common/Forms/models/ai-setting-form.model';
import { FullProfileAnimationModel } from '../../../../../Core/Dialogs/models/full-profile-animation.model';
import { AIChatCompletionApiModel, AIChatCompletionMessageApiModel } from '../../../../../api/ai/model/AI-chat-completion-api.model';
import { ResizeDirectionEnum } from '../../../../../Common/Forms/components/TextareaInput/textarea-input.component';
import { CoreDialogService } from '../../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { ConfigService } from '../../../../../Core/Config/services/config.service';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { ChatMessageTypeModel, ChatRole, QuickDistributionFormat } from '../../../../../api/ai/enums/AI-chat-completion.enum';
import { AiQuickTipEnums } from '../../../../../Common/Forms/enums/ai-quick-tip.enum';
import { AiGeneratedContentModel } from '../../../../../Common/Forms/models/ai-generated-content-card.model';
import { CompletionFormatEnum } from '../../../../../api/ai/enums/completion-format.enum';
import { QuickDistributionElementEnum } from '../../../../../Common/TargetingShared/enum/quick-distribution-element.enum';
import { DistributionTemplatesEnum } from '../../../../../Common/TargetingShared/enum/distribution-templates.enum';
import { elementToAiFormatMap, quickDistributionCommonElements, templateTypesToTemplateElementsMap } from './quick-distribution-elements.model';
import { CompletionPurposeEnum } from '@api/ai/enums/completion-purpose.enum';
import { CompletionTargetAudienceEnum } from '@api/ai/enums/completion-target-audience.enum';
import { CompletionToneEnum } from '@api/ai/enums/completion-tone.enum';

export interface AiDistributionContent {
    subjectLine: string;
    previewText: string;
    headline: string;
    subHeadline?: string;
    marketingPitch?: string;
    body: string;
    marketingConclusion?: string;
    aboutUs?: string;
    contactUs?: string;
}

export interface AiQuickDistributionOutput {
    aiGeneratedContent: AiDistributionContent;
    isPressRelease: boolean;
}


@Component({
    selector: 'agpr-ai-quick-distribution-dialog',
    templateUrl: './ai-quick-distribution-dialog.component.html',
    styleUrls: ['../../../../../Common/Dialogs/components/AIHelperDialog/AI-helper-dialog.component.scss', './ai-quick-distribution-dialog.component.scss'],
    standalone: false
})

export class AIQuickDistributionDialogComponent extends AbstractAiDialogComponent {
    readonly buttonEnum = ButtonTypesEnum;
    readonly resizeDirectionEnum = ResizeDirectionEnum;
    readonly minPanelWidth = 1180;
    readonly maxPanelWidth = 1380;

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() title: string = '';
    @Input() editState: boolean = false;
    @Input() templateType: DistributionTemplatesEnum;
    @Input() aiReferenceId: string;

    aiSettings: AiSettingFormModel = new AiSettingFormModel();

    hideSlideOverlay: boolean = false;
    agreedDisclaimer: boolean = false;
    loading: boolean = false;
    doneStreaming = true;
    hasGenerated: boolean = false;
    failedGenerateBody: boolean = false;
    canGenerate: boolean = false;
    showGeneratedContent: boolean = false;
    message: AIChatCompletionMessageApiModel;

    @ViewChild('quickTipContainer') quickTipContainer: ElementRef;

    quickTipInfo = {
        header: '',
        title: '',
        hints: []
    };

    constructor(
        coreDialogService: CoreDialogService,
        public localizationService: LocalizationService,
        public configService: ConfigService,
        public aiHelperDialogService: AbstractAIHelperDialogService,
        public messageService: MessageService
    ) {
        super(coreDialogService, 'AIHelper');
        this.aiSettings.formatConfig = this.aiHelperDialogService.getConfig();
    }

    showQuickTip(type: AiQuickTipEnums) {
        if (!this.quickTipContainer) {
            return;
        }
        // show div
        this.quickTipContainer.nativeElement.style.display = 'block';
        this.quickTipInfo.header = this.aiHelperDialogService.getQuickTipHeader(type);
        this.quickTipInfo.hints = this.aiHelperDialogService.getQuickTipHints(type);
    }

    generate(): void {
        this.generatedContent = '';
        this.hasGenerated = false;
        this.failedGenerateBody = false;
        this.showGeneratedContent = true;
        this.loading = true;
        this.doneStreaming = false;

        this.initializeDistributionElements();
        const format = this.getFormat() === CompletionFormatEnum.PRESS_RELEASE_DISTRIBUTION ?
            QuickDistributionFormat.QUICK_DISTRIBUTION_PRESSRELEASE : QuickDistributionFormat.QUICK_DISTRIBUTION_EMAIL;
        this.setRequestMessage(ChatMessageTypeModel.QUICK_DISTRIBUTION, format);
        this.fetchData(this.configService.getConfig().apiUrl + '/ai/chat/completions', null, this.aiReferenceId);
    }

    // we should use angular forms here but
    validateAiSettings(): void {
        const hasKeyMessage = this.aiSettings.keyMessages.filter(keyMessage => keyMessage.trim().length).length !== 0;

        const hasLanguage = (this.aiSettings.customLanguage && this.aiSettings.customLanguage?.trim() !== '') ||
            (this.aiSettings.language && this.aiSettings.language?.trim() !== '');

        const hasPurpose = (this.aiSettings.customPurpose && this.aiSettings.customPurpose?.trim() !== '') ||
            (this.aiSettings.purpose && this.aiSettings.purpose?.trim() !== '' && this.aiSettings.purpose?.trim() !== CompletionPurposeEnum.CUSTOM);

        const hasTargetAudience = (this.aiSettings.customTargetAudience && this.aiSettings.customTargetAudience?.trim() !== '') ||
            (this.aiSettings.targetAudience && this.aiSettings.targetAudience?.trim() !== '' &&
            this.aiSettings.targetAudience?.trim() !== CompletionTargetAudienceEnum.CUSTOM);

        const hasTone = (this.aiSettings.customTone && this.aiSettings.customTone?.trim() !== '') ||
            (this.aiSettings.tone && this.aiSettings.tone?.trim() !== '' && this.aiSettings.tone?.trim() !== CompletionToneEnum.CUSTOM);


        this.canGenerate = hasKeyMessage && hasPurpose && hasLanguage && hasTargetAudience && hasTone;
    }

    regenerateSingleElement(aiType: string): void {
        this.generatedContent = '';
        this.doneStreaming = false;
        const format = elementToAiFormatMap.get(this.aiSettings.formatConfig.defaultFormat).get(aiType as QuickDistributionElementEnum);
        const regenerationContext = {};
        this.contentElements.forEach(section => {
            regenerationContext[section.id] = section.value || section.history[section.currentIndex];
        });
        this.setRequestMessage(ChatMessageTypeModel.QUICK_DISTRIBUTION_REGENERATION, format, regenerationContext);
        this.fetchData(this.configService.getConfig().apiUrl + '/ai/chat/completions', aiType, this.aiReferenceId);
    }

    apply(): void {
        const aiGeneratedContent = {} as AiDistributionContent;

        this.contentElements.forEach(item => {
            aiGeneratedContent[item.id] = item.value;
        });

        this.closeDialog({
            aiGeneratedContent,
            isPressRelease: (this.aiSettings.formatConfig.defaultFormat === CompletionFormatEnum.PRESS_RELEASE_DISTRIBUTION)
        } as AiQuickDistributionOutput);
    }

    initializeDistributionElements(): void {
        this.contentElements = [];
        const elements = templateTypesToTemplateElementsMap
            .get(this.templateType)
            .get(this.aiSettings.formatConfig.defaultFormat);

        this.contentElements = [
            ...quickDistributionCommonElements.map(x => new AiGeneratedContentModel(x)),
            ...elements.map(x => new AiGeneratedContentModel(x))
        ];
    }

    setRequestMessage(chatMessageType: ChatMessageTypeModel, format: QuickDistributionFormat, regenerationContext?: any): void {
        const deduplicatedKeyMessages = Array.from(new Set(this.aiSettings.keyMessages.filter(msg => msg.trim().length)));
        const message: any = {
            keyMessages: deduplicatedKeyMessages,
            quotes: this.aiSettings.quotes.map(q => q.getApiModel()).filter(q => q != null),
            format: format,
            languageTag: this.aiSettings.language,
            customLanguage: this.aiSettings.customLanguage,
            tone: this.aiSettings.tone,
            customTone: this.aiSettings.customTone,
            targetAudience: this.aiSettings.targetAudience,
            customTargetAudience: this.aiSettings.customTargetAudience,
            purpose: this.aiSettings.purpose,
            customPurpose: this.aiSettings.customPurpose,
            length: this.aiSettings.length
        };
        message.generateMarketingPitch = this.templateType === DistributionTemplatesEnum.DISTRIBUTION_TEMPLATE_MARKETING
            && this.getFormat() === CompletionFormatEnum.EMAIL;
        message.generateMarketingConclusion = message.generateMarketingPitch;
        if (regenerationContext) {
            message.regenerationContext = regenerationContext;
        }

        this.message = {
            role: ChatRole.USER,
            chatMessageType: chatMessageType,
            message: JSON.stringify(message)
        };
    }

    extractContent(aiType?: string) {
        if (aiType) {
            const changedItem = this.contentElements.find(x => x.id === aiType);
            if (changedItem) {
                this.setItemContent(changedItem);
            }
        } else {
            this.contentElements.forEach(item => {
                this.setItemContent(item);
            });
        }
    }

    // set the content while streaming
    setItemContent(item: AiGeneratedContentModel) {
        try {
            const aiType = item.id;
            const jsonValue = this.setInsightJson(this.generatedContent)?.[aiType];
            if (jsonValue && isString(jsonValue)) {
                item.value = jsonValue
                    .replace(/<\/p>(\\n)*<p>/g, '</p><br><p>')
                    .replace(/\\n/g, '<br>')
                    .replace(/\\"/g, '"')
                    .trimStart();
            }
            item.loading = true;
            item.aiResponse = this.generatedContent;
        } catch (e) {
            // fail safe so the app doesn't crash
        }
    }

    onComplete(aiType?: string): void {
        if (aiType) {
            const changedItem = this.contentElements.find(x => x.id === aiType);
            if (changedItem) {
                this.setItemContentComplete(changedItem);
            }
        } else {
            this.contentElements.forEach(item => {
                this.setItemContentComplete(item);
            });
        }
        this.generatedContent = '';
        this.loading = false;
        this.doneStreaming = true;
        this.hasGenerated = true;
    }

    // set the content on completion (little bit different than setItemContent)
    setItemContentComplete(item: AiGeneratedContentModel) {
        try {
            const aiType = item.id;
            const jsonValue = JSON.parse(this.generatedContent)?.[aiType];
            if (jsonValue && isString(jsonValue)) {
                item.value = jsonValue
                    .replace(/<\/p>(\\n)*<p>/g, '</p><br><p>')
                    .replace(/\\n/g, '<br>')
                    .replace(/\\"/g, '"')
                    .trimStart();
            }
            item.loading = true;
            item.aiResponse = this.generatedContent;
            item.history.push(item.value);
            item.currentIndex = item.history.length - 1;
        } catch (e) {
            // fail safe so the app doesn't crash
        }
    }

    getRequestModel(): AIChatCompletionApiModel {
        return { messages: [this.message] };
    }

    getFormat(): CompletionFormatEnum {
        return this.aiSettings.formatConfig.defaultFormat;
    }

    handleError(aiType?: string): void {
        this.messageService.add(
            {
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Common.dialog.aiHelper.error.title'),
                detail: this.localizationService.transform('Common.dialog.aiHelper.error.description')
            }
        );
        this.loading = false;
        this.hasGenerated = true;
        this.doneStreaming = true;

        if (aiType) {
            const element = this.contentElements.find(x => x.id === aiType);
            element.loading = false;
            if (element.history.length) {
                element.value = element.history[element.currentIndex];
            }
            if (element.id === QuickDistributionElementEnum.body) {
                this.failedGenerateBody = !element.value;
            }
        } else {
            this.contentElements.forEach(item => {
                item.placeholder = 'Common.ai.generatedContentCard.valueNotGeneratedPlaceholder';
            });
        }
    }

    close(): void {
        this.hideSlideOverlay = true;
        this.aiConversationStack = [];
        this.closeDialog();
    }
}
